import { Injectable } from '@angular/core'
import {
  RootState,
  selectCwTransactionsSessionCacheStatus,
  selectExtTransactionsSessionCacheStatus,
  selectFlexExternalPositionsSessionCacheStatus,
  selectFlexExternalTransactionsSessionCacheStatus,
  selectFlexPositionsSessionCacheStatus,
  selectFlexTransactionsSessionCacheStatus,
  selectPositionsSessionCacheStatus,
  SessionCacheName,
  SessionCacheStatus,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import { Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

const EVENT_NAME = 'heliosSessionCacheLoadTiming'

/**
 * Monitors session cache status in redux, and reports load durations to FullStory
 * We start the timer once we get the STARTED status, and report durations for PARTIAL and DONE states.
 *
 * Caveat: for extremely short load times like account 4, ExtTx may not send a STARTED status so those load times may be missed.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionCacheTimerService {
  sessionLoadStartTimes: Map<string, number> = new Map()

  private readonly _destroyed$ = new Subject<void>()

  constructor(
    private readonly store: Store<RootState>,
    private readonly telemetryService: TelemetryService,
  ) {}

  initialize() {
    this.store
      .select(selectCwTransactionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status =>
        this.handleLoadTiming(SessionCacheName.CwTransactions, status),
      )

    this.store
      .select(selectExtTransactionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status =>
        this.handleLoadTiming(SessionCacheName.ExtTransactions, status),
      )

    this.store
      .select(selectPositionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status =>
        this.handleLoadTiming(SessionCacheName.Positions, status),
      )

    this.store
      .select(selectFlexPositionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status =>
        this.handleLoadTiming(SessionCacheName.FlexPositions, status),
      )

    this.store
      .select(selectFlexExternalPositionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status =>
        this.handleLoadTiming(SessionCacheName.FlexExternalPositions, status),
      )

    this.store
      .select(selectFlexTransactionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status =>
        this.handleLoadTiming(SessionCacheName.FlexTransactions, status),
      )

    this.store
      .select(selectFlexExternalTransactionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status =>
        this.handleLoadTiming(
          SessionCacheName.FlexExternalTransactions,
          status,
        ),
      )
  }

  cleanup() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  /**
   * Record metrics for session load times, for both DONE and PARTIAL states.
   * We record the start times for each cache in a map, and when we hit PARTIAL or DONE, we report the duration in MS.
   * Event params:
   *   Positions, ExtTransactions, CwTransactions (+ their Partial variants)
   *   FlexPositions, FlexExternalPositions, FlexTransactions, FlexExternalTransactions
   *   (+ their Partial variants)
   * Event values: duration in MS since we saw the 'Started' status.
   *
   * We MUST ONLY report the duration ONCE, so the startTime is removed from the map to indicate it's been reported
   *
   * @internal for test
   */
  handleLoadTiming(
    name: SessionCacheName,
    sessionCacheStatus: SessionCacheStatus,
  ) {
    const startTime = this.sessionLoadStartTimes.get(name)
    const now = Date.now()

    switch (sessionCacheStatus) {
      //reset timer when UNKNOWN is seen - this happens when the session changes.
      //same deal with ERROR - we don't want to report error times
      case SessionCacheStatus.ERROR:
      case SessionCacheStatus.UNKNOWN: {
        this.sessionLoadStartTimes.delete(name)
        this.sessionLoadStartTimes.delete(name + SessionCacheStatus.PARTIAL)
        return
      }

      case SessionCacheStatus.STARTED: {
        if (startTime) return

        // start timer, for both 'Done' and 'Partial' status.  When we report the event, these values are deleted so we don't report again
        this.sessionLoadStartTimes.set(name, now)
        this.sessionLoadStartTimes.set(name + SessionCacheStatus.PARTIAL, now)
        return
      }

      //PARTIAL is a special state: we need to keep going until DONE happens.  We'll delete the 'partial' start time to indicate we've already reported
      case SessionCacheStatus.PARTIAL: {
        if (!startTime) return

        const partialName = name + SessionCacheStatus.PARTIAL
        if (!this.sessionLoadStartTimes.has(partialName)) return

        this.telemetryService.emitCustomEvent(EVENT_NAME, {
          sessionCacheName: partialName,
          durationMs: now - startTime,
        })

        this.sessionLoadStartTimes.delete(partialName)
        return
      }

      // DONE is the end state: report the event, and clear the startTime so we don't report again
      case SessionCacheStatus.DONE:
        {
          if (!startTime) return
          this.telemetryService.emitCustomEvent(EVENT_NAME, {
            sessionCacheName: name,
            durationMs: now - startTime,
          })
          //clear the timer, so we don't send another event if we get 'Done' or 'Partial' Status again
          this.sessionLoadStartTimes.delete(name)
          this.sessionLoadStartTimes.delete(name + SessionCacheStatus.PARTIAL)
        }
        return
    }
  }
}
